import React from 'react';

import asyncCaptchaLoader from './async-captcha-loader';

import { dispatchMetric } from '../../../metrics';
import { getTime, getDiff } from '../../utils/time';
import getHash from '../../utils/get-hash';
import { fakeFunction } from '../../utils/promise';

import config from '../../../generated/config';
const { captchaSite: sitekey, captchaLoadTimeout } = config;

let startTime, executeTime;
const id = getHash();

const dispatchDuration = (name, time, state, error) => {
  try {
    dispatchMetric('login:captcha', {
      id,
      name,
      time: getDiff(time),
      state,
      error
    });
  } catch (error) {
    console.error(error);
  }
};

const INTERVAL_CHECK = 30;
//const TIMEOUT_LIMIT = 1800

class Recaptcha extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      widgetId: null,
      isRendered: false,
      isExpired: false,
      hasHash: false
    };

    this.promiseResolve = {
      start: fakeFunction,
      ready: fakeFunction,
      execute: fakeFunction
    };

    this.promiseReject = {
      error: fakeFunction
    };

    this.intervalIdLibLoaded = null;
    this.captchaElement = null;

    this.startReadyPromise();

    window.doreset = false;
  }

  dispatchMetrics(title, time, error) {
    dispatchDuration(title, time, { ...this.state }, error);
  }

  componentWillUnmount() {
    clearInterval(this.intervalIdLibLoaded);
  }

  isCaptchaLibLoaded() {
    const { grecaptcha } = this.props;
    if (grecaptcha) {
      const { ready, render, reset, getResponse, execute } = grecaptcha;
      return (
        typeof ready === 'function' &&
        typeof render === 'function' &&
        typeof reset === 'function' &&
        typeof getResponse === 'function' &&
        typeof execute === 'function'
      );
    }
    return false;
  }

  hasWidgetId() {
    return this.state.widgetId !== null;
  }

  isLibReady() {
    return this.isCaptchaLibLoaded() && this.hasWidgetId();
  }

  checkLibIsLoaded() {
    if (this.isCaptchaLibLoaded()) {
      clearInterval(this.intervalIdLibLoaded);
      this.promiseResolve.start();
      this.dispatchMetrics('lib: loaded', startTime);

      this.props.grecaptcha.ready(this.setCaptchaReady.bind(this));
      this.renderCaptcha();
    }
  }

  setCaptchaReady() {
    this.setState({ isRendered: true }, () => {
      this.dispatchMetrics('captcha: ready', startTime);
      this.promiseResolve.ready();
    });
  }

  bindStartResolve(startResolve) {
    if (typeof startResolve === 'function') {
      this.promiseResolve.start = startResolve;
    }
  }

  bindReadyResolve(readyResolve) {
    if (typeof readyResolve === 'function') {
      this.promiseResolve.ready = readyResolve;
    }
  }

  bindExecuteResolve(executeResolve) {
    if (typeof executeResolve === 'function') {
      this.promiseResolve.execute = executeResolve;
    }
  }

  bindErrorReject(errorReject) {
    if (typeof errorReject === 'function') {
      this.promiseReject.error = errorReject;
    }
  }

  startReadyPromise() {
    let rejectTimer;
    startTime = getTime();
    this.intervalIdLibLoaded = setInterval(this.checkLibIsLoaded.bind(this), INTERVAL_CHECK);

    new Promise((resolve, reject) => {
      rejectTimer = setTimeout(() => reject('CAPTCHA_LIB_NEVER_LOADED'), captchaLoadTimeout);
      this.bindStartResolve(resolve);
    })
      .then(() => clearTimeout(rejectTimer))
      .catch(error => this.dispatchMetrics('lib: load', startTime, error));
  }

  captchaReady() {
    return new Promise(resolve => {
      if (this.state.isRendered) {
        resolve();
      } else {
        this.bindReadyResolve(resolve);
      }
    });
  }

  renderCaptcha() {
    const widgetId = this.props.grecaptcha.render(this.captchaElement, {
      sitekey,
      size: 'invisible',
      callback: response => {
        this.setState({ isExpired: false, hasHash: true }, () =>
          this.promiseResolve.execute(response || '')
        );
      },
      'expired-callback': () => this.setState({ isExpired: true }),
      'error-callback': () =>
        this.promiseReject.error({ message: 'recaptcha external error', errorCode: 'unknown' })
    });
    this.setState({ widgetId });
  }

  async whenLibReadyGetResponse() {
    return new Promise((resolve, reject) => {
      this.bindExecuteResolve(response => {
        resolve(response);
      });
      this.bindErrorReject(error => {
        reject(error);
      });
    });
  }

  execute() {
    if (this.isLibReady()) {
      this.props.grecaptcha.execute(this.state.widgetId);
    }
  }

  reset() {
    if (this.isLibReady()) {
      this.setState(
        { isExpired: false, hasHash: false },
        this.props.grecaptcha.reset(this.state.widgetId)
      );
    }
  }

  getResponse() {
    return new Promise(async (resolve, reject) => {
      if (this.isCaptchaLibLoaded()) {
        executeTime = getTime();

        this.whenLibReadyGetResponse().then(resolve).catch(reject);

        await this.captchaReady();

        const response = this.props.grecaptcha.getResponse();
        const { hasHash, isExpired } = this.state;
        if (hasHash && !isExpired && response) {
          resolve(response);
        } else {
          this.execute();
        }
      } else {
        reject('CAPTCHA_LIB_NEVER_LOADED');
      }
    })
      .then(response => {
        this.dispatchMetrics('execute: resolve', executeTime);
        return response;
      })
      .catch(error => {
        this.dispatchMetrics('execute: reject', executeTime, error);
        throw error;
      });
  }

  render() {
    return <div ref={ref => (this.captchaElement = ref)} />;
  }
}

export default asyncCaptchaLoader('grecaptcha', Recaptcha, ['getResponse', 'reset']);
